import { ReactNode, forwardRef, useRef } from 'react';

import { Box } from '@mui/material';

import useVerticalScrollbar from '@/shared/lib/hooks/useVerticalScrollbar';

interface ScrollAreaProps {
  children: ReactNode;
  /**
   * When set, ScrollArea self-bounds to this height and scrolls its content
   * in-flow (no flex parent required, no negative-inset overhang) — safe inside
   * `overflow:hidden` MUI Menu papers. When omitted, ScrollArea fills its flex
   * parent (default; used by all list/dialog consumers).
   */
  maxHeight?: number | string;
}

const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(({ children, maxHeight }, ref) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useVerticalScrollbar(outerRef, {
    getScroller: () => scrollRef.current,
    getTrackParent: (_scroller, container) => container,
    getTrackBounds: (scroller) => ({ top: 0, height: scroller.clientHeight }),
    trackTransform: 'translate(12px, 0px)',
  });

  const attachScroller = (el: HTMLDivElement | null) => {
    scrollRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };

  if (maxHeight != null) {
    return (
      <Box ref={outerRef} sx={{ position: 'relative', width: '100%' }}>
        <Box
          ref={attachScroller}
          data-scroll-container
          sx={{
            maxHeight,
            padding: '8px',
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            scrollbarWidth: 'none',
            overscrollBehavior: 'contain',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {children}
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={outerRef} sx={{ position: 'relative', width: '100%', flex: 1, minHeight: 0 }}>
      <Box
        ref={attachScroller}
        data-scroll-container
        sx={{
          position: 'absolute',
          top: '-8px',
          bottom: '-8px',
          left: '-8px',
          right: '-8px',
          paddingInline: '8px',
          paddingBlock: '8px',
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          scrollbarWidth: 'none',
          overscrollBehavior: 'contain',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {children}
      </Box>
    </Box>
  );
});

ScrollArea.displayName = 'ScrollArea';

export default ScrollArea;
